/**
 * Context assembly + error wording for "ask the AI about this passage"
 * (packages/server/src/api/script.ts).
 *
 * WHAT GETS SENT, and why it is two things. The HIGHLIGHT says what the
 * question is about; the SCRIPT says what the video is. Neither answers a real
 * question on its own — "is this clear for a ten-year-old?" depends entirely on
 * what the lines before it were allowed to assume — so both travel, separately,
 * and the highlight is never merely quoted somewhere inside the script.
 *
 * WHOLE SCRIPT WHEN IT FITS. A script for this channel is typically a few
 * hundred lines and goes up ENTIRE; the head+middle+tail sampler
 * (features/design/sample.ts, shared with the design theme) only engages past
 * the caps, and when it does the request says so, so the prompt can tell the
 * model the gaps are sampling artifacts rather than jump cuts. The board can
 * hold 50,000 rows (docs/01), so an unbounded send is not an option — but
 * "sample everything just in case" would throw away the whole-video reading on
 * the scripts that actually fit, which is the only reason this feature is
 * better than pasting three lines into a chat window.
 *
 * The caps mirror the server's defaults. This is COOPERATION, not a trust
 * boundary: api/script.ts enforces its own configured caps and 400s an
 * oversized request rather than silently truncating it.
 */
import { sampleScript } from "../design/sample.js";

/** Script lines one request may carry. `DEFAULT_BRAINSTORM_MAX_SCRIPT_LINES`. */
export const BRAINSTORM_MAX_SCRIPT_LINES = 400;

/** Script characters one request may carry. `DEFAULT_BRAINSTORM_MAX_SCRIPT_CHARS`. */
export const BRAINSTORM_MAX_SCRIPT_CHARS = 24_000;

/** Highlighted characters one request may carry. `DEFAULT_BRAINSTORM_MAX_SELECTION_CHARS`. */
export const BRAINSTORM_MAX_SELECTION_CHARS = 8_000;

/** Highlighted lines one request may carry — the route's ajv ceiling. */
export const BRAINSTORM_MAX_SELECTION_LINES = 400;

/** One line's cap, matching the route's per-item `maxLength`. */
export const BRAINSTORM_MAX_LINE_CHARS = 4_000;

/**
 * Earlier turns replayed on a follow-up. Bounded because history is re-sent in
 * FULL every time, so each kept turn is paid for again on every later question.
 * Matches the route's `MAX_HISTORY_TURNS`.
 */
export const BRAINSTORM_MAX_HISTORY_TURNS = 6;

export interface BrainstormContext {
  /** The script in reading order — whole, or sampled to fit. */
  script: string[];
  /** True when `script` was thinned; the prompt then explains the gaps. */
  sampled: boolean;
  /** Non-empty script lines the board actually has. */
  totalLines: number;
  /** The highlighted lines, in script order. */
  selection: string[];
  /** Highlighted lines that did NOT fit — reported, never silently dropped. */
  selectionDropped: number;
}

/**
 * Fit the highlighted lines inside the request's budget, reporting what did not
 * fit.
 *
 * The highlight is NEVER sampled the way the script is — it is what the
 * question is about, and thinning it would change the question. So when one is
 * genuinely too big to send whole it is cut from the END and the overflow is
 * COUNTED, which is the whole reason this returns a number: a panel that says
 * "12 more lines not sent" is honest, and one that silently asks about less
 * than the writer highlighted is not.
 */
export function capSelection(lines: readonly string[]): {
  selection: string[];
  dropped: number;
} {
  const selection: string[] = [];
  let budget = BRAINSTORM_MAX_SELECTION_CHARS;
  let dropped = 0;
  for (const raw of lines) {
    const line = raw.trim().slice(0, BRAINSTORM_MAX_LINE_CHARS);
    if (line.length === 0) continue;
    if (selection.length >= BRAINSTORM_MAX_SELECTION_LINES || budget <= 0) {
      dropped += 1;
      continue;
    }
    if (line.length > budget) {
      // Keep the head of the line that overflows rather than dropping it: a
      // highlight that ends mid-sentence still reads as the passage in
      // question, where a missing final line just looks like a shorter one.
      selection.push(line.slice(0, budget));
      budget = 0;
      continue;
    }
    selection.push(line);
    budget -= line.length;
  }
  return { selection, dropped };
}

/**
 * Build one request's context from every script line on the board and the lines
 * the writer highlighted. The two are capped INDEPENDENTLY — see `capSelection`
 * for why only one of them may be thinned.
 */
export function buildBrainstormContext(
  allLines: readonly string[],
  highlighted: readonly string[],
): BrainstormContext {
  const clean = allLines.map((line) => line.trim()).filter((line) => line.length > 0);
  const script = sampleScript(clean, {
    maxLines: BRAINSTORM_MAX_SCRIPT_LINES,
    maxChars: BRAINSTORM_MAX_SCRIPT_CHARS,
  });
  const { selection, dropped } = capSelection(highlighted);

  return {
    script,
    sampled: script.length < clean.length,
    totalLines: clean.length,
    selection,
    selectionDropped: dropped,
  };
}

/**
 * What the panel says when a request fails. Every code here is one the route
 * can actually answer with (see api/script.ts's status list) — a message for a
 * status the server never sends would be dead text that reads as a real
 * possibility.
 *
 * `model` is the one the server tried, when it says so: with a picker on
 * screen, "which model was refused?" is the whole question.
 */
export function brainstormErrorMessage(status: number, code: string, model?: string): string {
  return commonErrorMessage(status, code, model) ?? "Couldn't get an answer. Try again.";
}

/**
 * Error wording for the TRANSLATE route (one answer, another language). The
 * route shares the question route's statuses, so the wording is shared too —
 * except where the fix differs: a rejected language tag, and an empty
 * translation (there is no question to rephrase).
 */
export function translateErrorMessage(status: number, code: string, model?: string): string {
  if (code === "invalid-language") {
    return "That isn't a language the translator accepts. Pick one from the list.";
  }
  if (code === "unusable-response") {
    return "The model came back empty. Try translating again.";
  }
  return commonErrorMessage(status, code, model) ?? "Couldn't translate the answer. Try again.";
}

/**
 * The branches the question and translate routes share. `null` for a status
 * neither recognises, so each caller supplies its own last line.
 */
function commonErrorMessage(status: number, code: string, model?: string): string | null {
  if (code === "brainstorm-not-configured") {
    return "AI is not configured on this server yet.";
  }
  if (code === "server-draining") {
    return "The server is restarting — try again in a few seconds.";
  }
  if (code === "rate-limited") {
    return "You've asked a lot of questions recently — give it a minute.";
  }
  if (code === "too-many-concurrent") {
    return "Another question is still running — wait for it to finish.";
  }
  if (code === "too-many-lines" || code === "too-many-chars") {
    return "This script is too long to send. Ask about a smaller board.";
  }
  if (code === "selection-too-long") {
    return "That's too much highlighted text — select a shorter passage.";
  }
  if (code === "unusable-response") {
    return "The model came back empty. Try rephrasing the question.";
  }
  if (code === "invalid-model") {
    return "That model id isn't a shape the provider accepts. Pick one from the list.";
  }
  const upstream = upstreamMessage(code, model);
  if (upstream !== null) return upstream;
  if (status === 403) return "You don't have permission to use AI on this board.";
  if (status === 404) return "This board no longer exists.";
  return null;
}

/**
 * The provider call itself failed. One "the AI provider failed to answer, try
 * again" covered all of these, which was wrong in the cases that matter most:
 * an unaccepted model, an unpaid account and a rejected key are each hopeless
 * to retry and each need a DIFFERENT person to do a different thing. The
 * server classifies from the upstream status alone (never its response body)
 * and sends the class as the code — see ai/providers/types.ts
 * `upstreamFailureCode`.
 *
 * `null` when `code` is not one of these, so the caller falls through to its
 * own status handling.
 */
export function upstreamMessage(code: string, model?: string): string | null {
  const named = model === undefined || model === "" ? "That model" : `"${model}"`;
  switch (code) {
    case "upstream-model-rejected":
      return `${named} wouldn't take this request. Pick a different model.`;
    case "upstream-unauthorized":
      return "The server's AI key was rejected. An admin needs to check it.";
    case "upstream-payment-required":
      return "The AI account is out of credit. An admin needs to top it up.";
    case "upstream-rate-limited":
      return "The AI provider is rate-limiting this key. Give it a minute.";
    case "upstream-timeout":
      return "The model took too long to answer. Try a shorter question, or a faster model.";
    case "upstream-unreachable":
      return "The server couldn't reach the AI provider. Check back shortly.";
    case "upstream-error":
      return "The AI provider failed to answer. Try again.";
    default:
      return null;
  }
}

/**
 * Error wording for the VISUAL route — same contract as
 * `brainstormErrorMessage`, different vocabulary: the fixes are image-side
 * (model choice, image quota), and telling the user to "rephrase the question"
 * about a failed render would send them down the wrong path.
 */
export function visualErrorMessage(status: number, code: string): string {
  if (code === "image-generation-unsupported") {
    return "This server's AI model can't generate images. An admin can pick an image model in Settings.";
  }
  if (code === "brainstorm-not-configured") {
    return "AI is not configured on this server yet.";
  }
  if (code === "rate-limited" || code === "too-many-concurrent") {
    return "You've generated a lot of images recently — give it a minute.";
  }
  if (code === "unusable-response") {
    return "The model didn't return a usable image. Try again.";
  }
  if (code === "server-draining") {
    return "The server is restarting — try again in a few seconds.";
  }
  if (code === "upstream-model-rejected") {
    return "This server's image model wouldn't take the request. An admin can pick another in Settings.";
  }
  const upstream = upstreamMessage(code);
  if (upstream !== null) return upstream;
  if (status === 403) return "You don't have permission to use AI on this board.";
  return "Couldn't generate the visual. Try again.";
}
